import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { CreditCard } from "lucide-react";
import { formatDate, formatCentsSafe } from "@/lib/utils";
import type { SubscriptionDetailed } from "@/api/generated/client/models/SubscriptionDetailed";

interface CurrentSubscriptionCardProps {
  subscription: SubscriptionDetailed;
  tierDisplayName: string;
  scheduledTierDisplayName?: string; // Keep for potential future use
  totalUsage?: { total: number };
  creditBalance?: { availableBalanceCents: number; currency: string } | null;
}

export function CurrentSubscriptionCard({
  subscription,
  tierDisplayName,
  totalUsage,
  creditBalance,
}: CurrentSubscriptionCardProps) {
  const getStatusBadgeVariant = (status: string): "default" | "secondary" | "destructive" | "outline" => {
    switch (status) {
      case "active":
        return "default";
      case "past_due":
        return "destructive";
      case "canceled":
        return "secondary";
      default:
        return "outline";
    }
  };

  const getStatusLabel = (status: string): string => {
    switch (status) {
      case "active":
        return "Active";
      case "past_due":
        return "Past Due";
      case "canceled":
        return "Canceled";
      default:
        return status;
    }
  };

  // Get billing interval display text (default to Monthly for legacy subscriptions)
  const billingPeriod = subscription.billingPeriod || 'monthly';
  const billingIntervalText = billingPeriod === 'yearly' ? 'Yearly' : 'Monthly';

  // Calculate monetary value of usage (credits * credit rate)
  const usageCredits = totalUsage?.total || 0;
  const creditRateCents = subscription.tier?.creditRateCents || 0;
  const usageAmountCents = usageCredits * creditRateCents;

  return (
    <div className="max-w-7xl mx-auto">
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <CreditCard className="h-5 w-5" />
            Current Subscription
          </CardTitle>
          <CardDescription>
            Your active plan and usage details
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="grid gap-6 md:grid-cols-3 lg:grid-cols-4">
            {/* Plan Details */}
            <div className="space-y-2">
              <div className="text-sm text-muted-foreground">Plan</div>
              <div className="flex items-center gap-2">
                <div className="font-semibold text-lg">
                  {tierDisplayName}
                </div>
                <Badge variant={getStatusBadgeVariant(subscription.status)}>
                  {getStatusLabel(subscription.status)}
                </Badge>
              </div>
              <div className="text-xs text-muted-foreground">
                {billingIntervalText} billing
              </div>
            </div>

            {/* Billing Period */}
            <div className="space-y-2">
              <div className="text-sm text-muted-foreground">Renews At</div>
              <div className="font-medium">
                {formatDate(subscription.renewsAt)}
              </div>
              {!!subscription.canceledAt && (
                <div className="text-xs text-destructive font-medium">
                  Cancels at period end
                </div>
              )}
            </div>

            {/* Usage This Period */}
            <div className="space-y-2">
              <div className="text-sm text-muted-foreground">Usage This Period</div>
              <div className="flex items-baseline gap-2">
                <div className="font-semibold text-2xl">
                  {formatCentsSafe(usageAmountCents)}
                </div>
              </div>
              
            </div>

            {/* Credit Grants */}
            <div className="space-y-2">
              <div className="text-sm text-muted-foreground">Credit Grants</div>
              <div className="flex items-baseline gap-2">
                <div className="font-semibold text-2xl text-green-600">
                  {formatCentsSafe(creditBalance?.availableBalanceCents ?? 0)}
                </div>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
